import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string, placeId?: string) => void;
  placeholder?: string;
  type?: 'city' | 'address';
}

interface PlacePrediction {
  description: string;
  place_id: string;
}

export const LocationAutocomplete = ({
  value,
  onChange,
  placeholder = 'Select city from suggestions',
  type = 'city'
}: LocationAutocompleteProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const debounceRef = useRef<NodeJS.Timeout>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isValidatingRef = useRef(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPlacePredictions = async (input: string) => {
    const minLength = type === 'address' ? 5 : 3;
    if (!input || input.length < minLength) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-location', {
        body: { input, type: type === 'address' ? 'address' : 'autocomplete' }
      });

      if (error) throw error;

      if (data?.predictions) {
        setPredictions(data.predictions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching location predictions:', error);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    setValidationStatus('idle');
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchPlacePredictions(newValue);
    }, 300);
  };

  const handleSelectPrediction = (prediction: PlacePrediction) => {
    setInputValue(prediction.description);
    onChange(prediction.description, prediction.place_id);
    setShowSuggestions(false);
    setPredictions([]);
    setValidationStatus('valid');
  };

  const handleBlur = async () => {
    // Delay to allow click on suggestion
    setTimeout(async () => {
      // Only validate for address type, not city
      if (type !== 'address' || !inputValue || inputValue === value || isValidatingRef.current) {
        return;
      }

      // Auto-validate address on blur
      isValidatingRef.current = true;
      setValidationStatus('validating');
      setIsLoading(true);

      try {
        // Step 1: Validate if address exists
        const { data: validationData, error: validationError } = await supabase.functions.invoke('validate-location', {
          body: { input: inputValue, type: 'validate' }
        });

        if (validationError) throw validationError;

        if (validationData?.valid) {
          // Step 2: Geocode to get precise coordinates
          const placeId = validationData.place_id || null;
          
          if (placeId) {
            const { data: geocodedData, error: geocodeError } = await supabase.functions.invoke('validate-location', {
              body: { input: placeId, type: 'geocode' }
            });

            if (geocodeError) throw geocodeError;

            if (geocodedData) {
              // Success: update with geocoded address
              onChange(geocodedData.formatted_address, placeId);
              setInputValue(geocodedData.formatted_address);
              setValidationStatus('valid');
              toast.success(t('services.registerProvider.addressValidated'));
            } else {
              throw new Error('No geocoded data');
            }
          } else {
            throw new Error('No place_id');
          }
        } else {
          // Invalid address
          setValidationStatus('invalid');
          toast.error(t('services.registerProvider.addressCouldNotBeValidated'));
        }
      } catch (error) {
        console.error('Error validating address on blur:', error);
        setValidationStatus('invalid');
        toast.error(t('services.registerProvider.addressCouldNotBeValidated'));
      } finally {
        setIsLoading(false);
        isValidatingRef.current = false;
      }
    }, 200);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
      <Input
        id="location"
        type="text"
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        onFocus={() => {
          if (predictions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className={`pl-9 pr-9 ${
          validationStatus === 'valid' ? 'border-green-500 focus:border-green-500' : 
          validationStatus === 'invalid' ? 'border-destructive focus:border-destructive' : ''
        }`}
      />
      {isLoading && validationStatus === 'validating' && (
        <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-blue-500" />
      )}
      {!isLoading && validationStatus === 'valid' && (
        <CheckCircle2 className="absolute right-3 top-3 h-4 w-4 text-green-500" />
      )}
      {!isLoading && validationStatus === 'invalid' && (
        <AlertCircle className="absolute right-3 top-3 h-4 w-4 text-destructive" />
      )}
      {isLoading && validationStatus === 'idle' && (
        <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
      )}

      {showSuggestions && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              className="w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors flex items-center gap-2"
              onClick={() => handleSelectPrediction(prediction)}
            >
              <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm">{prediction.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
